import { create } from 'zustand';
import { SECTIONS, isSectionVisible, type Section } from '@/components/layout/nav-config';
import { useAuthStore } from './authStore';
import { usePermissionsStore } from './permissionsStore';
import { api } from '@/shared/api/client';

// Per-user navigation preferences. The server is the source of truth — we
// PUT to /user-preferences/<NAMESPACE> on every change (debounced) and GET
// once on login. localStorage is just a paint-order cache so the navbar
// renders the user's saved layout instantly on page load instead of
// flashing the default order while the server round-trip lands.
const STORAGE_KEY = 'mrpcomp:nav-prefs:v1';
const NAMESPACE = 'nav-prefs';
const SAVE_DEBOUNCE_MS = 500;

export type NavPrefs = {
  // Ordered list of section keys. Keys not in this list fall back to default
  // order (appended at the end). Missing entries allow new sections we add
  // later to show up automatically without clobbering user prefs.
  mainOrder: string[];
  // Per-section ordered list of sub-item `to` paths. Same fallback rule.
  subOrder: Record<string, string[]>;
  // Keys (section keys OR sub-item `to` paths) hidden from the nav. Hidden
  // items still exist as routes — users just don't see them in the chrome.
  hidden: string[];
  // Icon-only main nav — labels collapse when true.
  compact: boolean;
};

const DEFAULTS: NavPrefs = {
  mainOrder: [],
  subOrder: {},
  hidden: [],
  compact: false,
};

function normalize(v: unknown): NavPrefs {
  if (!v || typeof v !== 'object') return DEFAULTS;
  const o = v as Partial<NavPrefs>;
  return {
    mainOrder: Array.isArray(o.mainOrder) ? o.mainOrder : [],
    subOrder: o.subOrder && typeof o.subOrder === 'object' ? o.subOrder : {},
    hidden: Array.isArray(o.hidden) ? o.hidden : [],
    compact: !!o.compact,
  };
}

function loadCache(): NavPrefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    return normalize(JSON.parse(raw));
  } catch {
    return DEFAULTS;
  }
}

function saveCache(prefs: NavPrefs) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs)); }
  catch { /* quota exceeded — best effort */ }
}

let saveTimer: ReturnType<typeof setTimeout> | null = null;
function scheduleServerSave(prefs: NavPrefs) {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(async () => {
    saveTimer = null;
    if (!useAuthStore.getState().user) return;
    try {
      await api.put(`/user-preferences/${NAMESPACE}`, prefs);
    } catch { /* localStorage already has the truth — retry on next change */ }
  }, SAVE_DEBOUNCE_MS);
}

type NavPrefsState = NavPrefs & {
  hydrated: boolean;
  // In-place "design the menu" mode: when true the real nav tabs become
  // draggable (reorder) instead of navigating. Ephemeral — never persisted.
  designing: boolean;
  setDesigning: (v: boolean) => void;
  hydrate: () => Promise<void>;
  setMainOrder: (order: string[]) => void;
  setSubOrder: (sectionKey: string, order: string[]) => void;
  toggleHidden: (key: string) => void;
  setHidden: (key: string, hidden: boolean) => void;
  setCompact: (v: boolean) => void;
  reset: () => void;
};

type ServerRow = { value: NavPrefs; updated_at: string } | null;

export const useNavPrefs = create<NavPrefsState>((set, get) => ({
  ...loadCache(),
  hydrated: false,
  designing: false,
  setDesigning: (v) => set({ designing: v }),
  hydrate: async () => {
    try {
      const row = await api.get<ServerRow>(`/user-preferences/${NAMESPACE}`);
      if (row && row.value) {
        const next = normalize(row.value);
        set({ ...next, hydrated: true });
        saveCache(next);
        return;
      }
    } catch { /* fall through to defaults */ }
    set({ hydrated: true });
  },
  setMainOrder: (order) => {
    set({ mainOrder: order });
    const s = get();
    saveCache(s);
    scheduleServerSave({ mainOrder: s.mainOrder, subOrder: s.subOrder, hidden: s.hidden, compact: s.compact });
  },
  setSubOrder: (sectionKey, order) => {
    set({ subOrder: { ...get().subOrder, [sectionKey]: order } });
    const s = get();
    saveCache(s);
    scheduleServerSave({ mainOrder: s.mainOrder, subOrder: s.subOrder, hidden: s.hidden, compact: s.compact });
  },
  toggleHidden: (key) => {
    const cur = get().hidden;
    const next = cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key];
    set({ hidden: next });
    const s = get();
    saveCache(s);
    scheduleServerSave({ mainOrder: s.mainOrder, subOrder: s.subOrder, hidden: s.hidden, compact: s.compact });
  },
  setHidden: (key, hidden) => {
    const cur = get().hidden;
    const already = cur.includes(key);
    if (hidden && !already) set({ hidden: [...cur, key] });
    else if (!hidden && already) set({ hidden: cur.filter((k) => k !== key) });
    else return;
    const s = get();
    saveCache(s);
    scheduleServerSave({ mainOrder: s.mainOrder, subOrder: s.subOrder, hidden: s.hidden, compact: s.compact });
  },
  setCompact: (v) => {
    set({ compact: v });
    const s = get();
    saveCache(s);
    scheduleServerSave({ mainOrder: s.mainOrder, subOrder: s.subOrder, hidden: s.hidden, compact: s.compact });
  },
  reset: () => {
    set({ ...DEFAULTS });
    saveCache(DEFAULTS);
    scheduleServerSave(DEFAULTS);
  },
}));

// Reorder `defaults` by `order`, honoring the stored order first and then
// appending any items that aren't in `order` yet (e.g. a new section shipped
// in a later build). Unknown keys in `order` are silently dropped.
function applyOrder<T>(defaults: T[], keyOf: (x: T) => string, order: string[]): T[] {
  if (!order.length) return defaults;
  const byKey = new Map(defaults.map((d) => [keyOf(d), d]));
  const seen = new Set<string>();
  const out: T[] = [];
  for (const k of order) {
    const d = byKey.get(k);
    if (d && !seen.has(k)) { out.push(d); seen.add(k); }
  }
  for (const d of defaults) {
    if (!seen.has(keyOf(d))) out.push(d);
  }
  return out;
}

// Main sections in stored order, with hidden ones filtered out, role
// gating applied, AND RBAC permissions enforced. A section is dropped
// when (a) the user's roles don't intersect requiresAnyRole, OR (b) the
// effective permission level for the section's catalog node is
// 'hidden'. The router-level <PermissionGate> redundantly enforces (b)
// for direct-URL access.
export function useOrderedSections() {
  const mainOrder = useNavPrefs((s) => s.mainOrder);
  const hidden = useNavPrefs((s) => s.hidden);
  const userRoles = useAuthStore((s) => s.user?.roles || []);
  const effective = usePermissionsStore((s) => s.effective);
  const ordered = applyOrder(SECTIONS, (s) => s.key, mainOrder);
  return ordered
    .filter((s) => isSectionVisible(s, userRoles))
    .filter((s) => !hidden.includes(s.key))
    .filter((s) => {
      const node = s.node || s.key;
      return effective[node] !== 'hidden';
    });
}

// Sub-items for a section in stored order, hidden ones filtered out + RBAC
// gating. Returns the same item shape as nav-config.SubItem so callers
// don't care about prefs.
export function useOrderedSubItems(sectionKey: string | undefined) {
  const subOrder = useNavPrefs((s) => s.subOrder);
  const hidden = useNavPrefs((s) => s.hidden);
  const effective = usePermissionsStore((s) => s.effective);
  if (!sectionKey) return [];
  const section = SECTIONS.find((s) => s.key === sectionKey);
  if (!section) return [];
  const order = subOrder[sectionKey] || [];
  const ordered = applyOrder(section.sub, (it) => it.to, order);
  return ordered
    .filter((it) => !hidden.includes(it.to))
    .filter((it) => !it.node || effective[it.node] !== 'hidden');
}

// Resolve a section tab's landing route: the FIRST visible sub-item, in the
// user's custom order when one exists, else the configured order — so a main
// nav click always highlights the sub-tab that's visually first (e.g.
// Production lands on BOMs, not the static base '/manufacturing-orders').
// Sections with no sub items (Home) keep their base. Returns a resolver so
// MainNav can call it per-section without breaking the rules-of-hooks.
export function useSectionLanding(): (section: Section) => string {
  const subOrder = useNavPrefs((s) => s.subOrder);
  const hidden = useNavPrefs((s) => s.hidden);
  const effective = usePermissionsStore((s) => s.effective);
  const userRoles = useAuthStore((s) => s.user?.roles || []);
  const company = useAuthStore((s) => s.company);
  return (section: Section) => {
    if (!section.sub || section.sub.length === 0) return section.base;
    const order = subOrder[section.key];
    const ordered = order && order.length > 0 ? applyOrder(section.sub, (it) => it.to, order) : section.sub;
    const first = ordered
      .filter((it) => !hidden.includes(it.to))
      .filter((it) => !it.node || effective[it.node] !== 'hidden')
      .filter((it) => !it.requiresRole || userRoles.includes(it.requiresRole))
      .filter((it) => !it.requiresCompanyFlag || company?.[it.requiresCompanyFlag] === true)[0];
    return first?.to || section.base;
  };
}
